import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';

import { receiptsSelectors, trackReceiptEvent } from 'app-components/flux/receipts';
import { buildReceiptSplitSeed, type ReceiptSplitSeed } from 'app-components/flux/transactions/splitsHelpers';

import ReceiptDetailsDialog from 'components/Receipts/ReceiptDetailsDialog';
import ReceiptImage from 'components/Receipts/ReceiptImage';
import { useAppSelector } from 'hooks/redux';
import useFormatAsCurrency from 'hooks/useFormatAsCurrency';

type Props = {
  receiptId?: string;
  hasSplits: boolean;
  onCreateSplits: (seed: ReceiptSplitSeed) => void;
};

const ReceiptSection = ({ receiptId, hasSplits, onCreateSplits }: Props): React.JSX.Element | null => {
  const receipt = useAppSelector((state) =>
    receiptId ? receiptsSelectors.selectReceiptById(state, receiptId) : undefined,
  );
  const formatAsCurrency = useFormatAsCurrency();
  const [previewOpen, setPreviewOpen] = useState(false);
  const splitSeed = useMemo(() => (receipt ? buildReceiptSplitSeed(receipt) : undefined), [receipt]);

  if (!receipt) {
    return null;
  }

  const title = receipt.extraction?.merchant || receipt.name || 'Receipt';
  const transactionDate = receipt.extraction?.transactionDate
    ? DateTime.fromISO(receipt.extraction.transactionDate, { zone: 'utc' })
    : null;
  const dateLabel = transactionDate?.isValid ? transactionDate.toFormat('LLL d, yyyy') : undefined;
  const amountLabel =
    receipt.extraction?.total === undefined
      ? 'Amount unavailable'
      : formatAsCurrency(receipt.extraction.total, { signDisplay: 'auto' });
  const splitItems = splitSeed?.split.items;
  const lineItemCount = splitItems?.length ?? 0;
  const lineItemLabel = `${lineItemCount} items`;
  const splitSummary =
    receipt.extraction?.total === undefined ? lineItemLabel : `${lineItemLabel} · ${amountLabel} total`;

  return (
    <Card
      elevation={4}
      sx={{
        mb: 2,
        minWidth: 0,
        maxWidth: '100%',
        alignSelf: 'stretch',
        overflow: 'visible',
        border: 1,
        borderColor: 'border.main',
      }}
      data-testid={`receipt-transaction-section-${receipt.id}`}
    >
      <Box
        sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden', borderRadius: 'inherit' }}
        data-testid={`receipt-transaction-section-${receipt.id}-content`}
      >
        <ButtonBase
          onClick={() => {
            trackReceiptEvent({
              event: 'receiptDetailsView',
              properties: { location: receipt.transactionId ? 'transaction-detail' : 'create-new-transaction' },
            });
            setPreviewOpen(true);
          }}
          aria-label={`View ${title} receipt`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 2,
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            boxSizing: 'border-box',
            p: 2,
            textAlign: 'left',
            '&:hover, &:focus-visible': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Stack direction='row' alignItems='center' gap={1} sx={{ minWidth: 0, flex: 1 }}>
            <ReceiptImage
              documentId={receipt.documentId}
              localFileUri={receipt.localFileUri}
              mimeType={receipt.mimeType}
              alt={`${title} receipt`}
              width={42}
              height={42}
              borderRadius={1.5}
            />
            <Stack gap={0.5} px={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant='paragraphMedium' noWrap>
                {title}
              </Typography>
              {dateLabel ? (
                <Typography variant='captionRegular' color='text.secondary'>
                  {dateLabel}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
          <Typography
            variant='paragraphRegular'
            color='text.primary'
            sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
          >
            {amountLabel}
          </Typography>
          <ChevronRightIcon sx={{ color: 'text.secondary' }} aria-hidden />
        </ButtonBase>

        {splitSeed && !hasSplits ? (
          <ButtonBase
            onClick={() => {
              trackReceiptEvent({
                event: 'receiptCreateSplits',
                properties: { location: receipt.transactionId ? 'existing-transaction' : 'create-new-transaction' },
              });
              onCreateSplits(splitSeed);
            }}
            aria-label={`Create splits from receipt items ${splitSummary}`}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              gap: 2,
              width: '100%',
              minWidth: 0,
              maxWidth: '100%',
              boxSizing: 'border-box',
              borderTop: 1,
              borderColor: 'border.light',
              p: 2,
              '&:hover, &:focus-visible': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Stack direction='row' alignItems='center' gap={1} sx={{ minWidth: 0, flex: 1 }}>
              <IconButton
                component='span'
                size='large'
                variant='tonal'
                color='default'
                tabIndex={-1}
                aria-hidden
                sx={{ flexShrink: 0, pointerEvents: 'none' }}
              >
                <CallSplitRoundedIcon />
              </IconButton>
              <Stack gap={0.25} pl={0.5} sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                <Typography variant='paragraphMedium'>Create splits from receipt items</Typography>
                <Stack direction='row' gap={0.5} alignItems='center'>
                  <Typography variant='paragraphRegular' color='text.secondary'>
                    {lineItemLabel}
                  </Typography>
                  {receipt.extraction?.total === undefined ? null : (
                    <>
                      <Typography variant='paragraphRegular' color='text.disabled' aria-hidden>
                        ·
                      </Typography>
                      <Typography variant='paragraphRegular' color='text.secondary'>
                        {amountLabel} total
                      </Typography>
                    </>
                  )}
                </Stack>
              </Stack>
            </Stack>
            <ChevronRightIcon sx={{ color: 'text.secondary' }} aria-hidden />
          </ButtonBase>
        ) : null}
      </Box>

      <ReceiptDetailsDialog receipt={receipt} open={previewOpen} onClose={() => setPreviewOpen(false)} />
    </Card>
  );
};

export default ReceiptSection;
